import type {
  CreateCustomLineItemInput,
  CreateEntityFinancialRecordInput,
  CreateFinancialPeriodInput,
  CustomLineItemCategory,
  CustomLineItemStatementType,
} from '@/types/financial';
import {
  CSV_IMPORT_BS_DESTINATIONS,
  CSV_IMPORT_PL_DESTINATIONS,
} from './csv-parser';
import type { MappedRow } from './csv-import-flow';

export interface CsvImportDestinationOption {
  value: string;
  label: string;
  statementType: 'pl' | 'bs';
}

export const CSV_IMPORT_DESTINATION_OPTIONS: CsvImportDestinationOption[] = [
  ...CSV_IMPORT_PL_DESTINATIONS.map((value) => ({
    value,
    label: humanizeDestinationLabel(value),
    statementType: 'pl' as const,
  })),
  ...CSV_IMPORT_BS_DESTINATIONS.map((value) => ({
    value,
    label: humanizeDestinationLabel(value),
    statementType: 'bs' as const,
  })),
];

export function getDestinationOptions(statementType: 'pl' | 'bs'): CsvImportDestinationOption[] {
  return CSV_IMPORT_DESTINATION_OPTIONS.filter((option) => option.statementType === statementType);
}

export function toCustomLineItemStatementType(
  statementType: 'pl' | 'bs'
): CustomLineItemStatementType {
  return statementType === 'pl' ? 'profit_loss' : 'balance_sheet';
}

export function toCustomLineItemCategory(
  category: string,
  statementType: 'pl' | 'bs'
): CustomLineItemCategory {
  if (statementType === 'bs') {
    switch (category) {
      case 'current_asset':
      case 'non_current_asset':
      case 'current_liability':
      case 'non_current_liability':
      case 'equity':
        return category;
      default:
        return 'current_asset';
    }
  }

  switch (category) {
    case 'revenue':
      return 'revenue';
    case 'cogs':
      return 'cost_of_sales';
    case 'non_operating':
      return 'non_operating_expense';
    case 'personnel':
    case 'opex':
    default:
      return 'operating_expense';
  }
}

export interface CsvImportTargetDeps {
  getEntityRecord: (accountId: string) => Promise<{ id: string } | null>;
  createEntityRecord: (
    input: CreateEntityFinancialRecordInput
  ) => Promise<{ id: string }>;
  createPeriod: (input: CreateFinancialPeriodInput) => Promise<{ id: string }>;
}

export async function ensureCsvImportTarget(args: {
  accountId: string;
  entityFinancialRecordId?: string;
  periodInput: Omit<CreateFinancialPeriodInput, 'entityFinancialRecordId'>;
  deps: CsvImportTargetDeps;
}): Promise<{ entityFinancialRecordId: string; periodId: string }> {
  const { accountId, entityFinancialRecordId, periodInput, deps } = args;

  let resolvedEntityFinancialRecordId = entityFinancialRecordId;
  if (!resolvedEntityFinancialRecordId) {
    const existingRecord = await deps.getEntityRecord(accountId);
    if (existingRecord) {
      resolvedEntityFinancialRecordId = existingRecord.id;
    } else {
      const createdRecord = await deps.createEntityRecord({
        account_id: accountId,
        financial_template: 'trading',
      });
      resolvedEntityFinancialRecordId = createdRecord.id;
    }
  }

  const period = await deps.createPeriod({
    entityFinancialRecordId: resolvedEntityFinancialRecordId,
    ...periodInput,
  });

  return {
    entityFinancialRecordId: resolvedEntityFinancialRecordId,
    periodId: period.id,
  };
}

export interface MaterializeCustomRowsDeps {
  createCustomLineItem: (input: CreateCustomLineItemInput) => Promise<{ id: string }>;
  upsertCustomLineItemValue: (input: {
    customLineItemId: string;
    financialPeriodId: string;
    amount: number;
  }) => Promise<unknown>;
}

export async function materializeCustomImportRows(args: {
  rows: MappedRow[];
  entityFinancialRecordId: string;
  financialPeriodId: string;
  deps: MaterializeCustomRowsDeps;
}): Promise<MappedRow[]> {
  const { rows, entityFinancialRecordId, financialPeriodId, deps } = args;

  return Promise.all(
    rows.map(async (row) => {
      if (!row.createNewLabel || row.destinationCustomItemId) {
        return row;
      }

      const created = await deps.createCustomLineItem({
        entityFinancialRecordId,
        statementType: toCustomLineItemStatementType(row.statementType),
        category: toCustomLineItemCategory(row.category, row.statementType),
        label: row.createNewLabel,
      });

      await deps.upsertCustomLineItemValue({
        customLineItemId: created.id,
        financialPeriodId,
        amount: row.amount,
      });

      return {
        ...row,
        destinationKind: 'custom',
        destinationEnumType: null,
        destinationCustomItemId: created.id,
      };
    })
  );
}

export function buildMappingsToSave(rows: MappedRow[], accountId: string) {
  return rows
    .filter((row) => row.isNew && row.destinationKind)
    .map((row) => ({
      accountId,
      sourceLabel: row.normalisedLabel,
      destinationKind: row.destinationKind!,
      destinationEnumType: row.destinationEnumType,
      destinationCustomItemId: row.destinationCustomItemId,
      category: row.category,
      statementType: row.statementType,
    }));
}

function humanizeDestinationLabel(value: string): string {
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
